import {
  CreateMarketingCampaignRequest,
  CampaignType,
  DisplayMarketingCampaign,
  AudienceData,
} from "common/interfaces/KYDMarketing";
import { mapCustomAudiencesToServerFormat } from "./customAudienceMapper";

export interface CampaignFormValues {
  id?: string;
  event_ids: string[];
  name: string;
  campaign_type: CampaignType;
  budget?: {
    type: "daily" | "lifetime";
    amount: {
      amount: number;
      currency: string;
    };
  };
  budget_type: "campaign" | "adset";
  ad_account: {
    value: string;
    label: string;
  };
  custom_audiences?: Array<AudienceData>;
  audience_search_query?: string;
}

export interface AdAccount {
  id: string;
  name: string;
}

const transform = {
  input: ({
    campaign,
    adAccounts = [],
  }: // audiences = [],
  {
    campaign?: DisplayMarketingCampaign;
    adAccounts?: AdAccount[];
    // audiences?: AudienceData[];
  }): CampaignFormValues => {
    return {
      id: campaign?.id || "",
      event_ids: campaign?.event_ids || [],
      name: campaign?.name?.replace("KYD: ", "") || "",
      campaign_type: campaign?.campaign_type || CampaignType.CONVERSION,
      budget: campaign?.budget
        ? {
            type: campaign.budget.type,
            amount: {
              amount: campaign.budget.amount.units,
              currency: "USD",
            },
          }
        : undefined,
      budget_type: campaign?.budget ? "campaign" : "adset",
      custom_audiences:
        campaign?.targeting?.custom_audiences?.map((audience) => {
          const baseAudience = {
            id: audience.id,
            external_id: audience.external_id,
            name: audience.name,
            created_at: audience.metadata?.created_time,
            description: audience.metadata?.description,
          };

          if (audience.type === "csv") {
            return {
              ...baseAudience,
              audience_type: "csv" as const,
              file_key: audience.metadata?.file_key || "",
              mapping: audience.metadata?.mapping || [],
              schema_fields: audience.metadata?.schema_fields || [],
            };
          } else if (audience.type === "page_lookalike") {
            return {
              ...baseAudience,
              audience_type: "page_lookalike" as const,
              page_type: audience.metadata?.page_type as
                | "facebook"
                | "instagram",
              page_id: audience.metadata?.page_id || "",
              page_name: audience.metadata?.page_name || "",
              lookalike_ratio: audience.metadata?.lookalike_ratio || "",
            };
          } else if (audience.type === "page_engagers") {
            return {
              ...baseAudience,
              audience_type: "page_engagers" as const,
              page_type: audience.metadata?.page_type as
                | "facebook"
                | "instagram",
              page_id: audience.metadata?.page_id || "",
              page_name: audience.metadata?.page_name || "",
            };
          } else if (audience.type === "kyd") {
            return {
              ...baseAudience,
              audience_type: "kyd" as const,
              genres: audience.metadata?.genres || [],
              artists: audience.metadata?.artists || [],
              lookalike: audience.metadata?.lookalike_ratio
                ? {
                    name: audience.name || "",
                    ratio: audience.metadata.lookalike_ratio,
                  }
                : undefined,
            };
          } else if (audience.type === "meta") {
            return {
              ...baseAudience,
              audience_type: "meta" as const,
              // Meta audiences only store basic fields in metadata
              // Additional fields like subtype, data_source, etc. are not stored in MarketingCustomAudience
              subtype: "", // Default empty string since subtype is required but not stored in metadata
              time_created: audience.metadata?.created_time,
              time_updated: audience.metadata?.updated_time,
            };
          }

          // Fallback for unknown audience types - return as KYD audience
          return {
            ...baseAudience,
            audience_type: "kyd" as const,
            genres: [],
            artists: [],
          };
        }) || [],
      ad_account: campaign?.account_id
        ? {
            value: campaign.account_id,
            label:
              adAccounts?.find(
                (a) => a.id.replace("act_", "") === campaign.account_id
              )?.name || campaign.account_id,
          }
        : {
            value: adAccounts?.at(0)?.id || "",
            label: adAccounts?.at(0)?.name || "",
          },
    };
  },

  output: ({
    data,
  }: {
    data: CampaignFormValues;
  }): CreateMarketingCampaignRequest => {
    console.log("output campaign", data);
    return {
      id: data.id,
      event_ids: data.event_ids?.filter(Boolean) || [],
      name: data.name,
      budget:
        data.budget?.amount && data.budget_type === "campaign"
          ? {
              type: data.budget.type,
              amount: {
                amount: data.budget.amount.amount,
                currency: data.budget.amount.currency,
              },
            }
          : null,
      account_id: data.ad_account?.value || "",
      campaign_type: data.campaign_type,
      custom_audiences: data.custom_audiences
        ? mapCustomAudiencesToServerFormat(data.custom_audiences)
        : [],
    };
  },
};

export default transform;
